import React from 'react';
import { useRequest } from 'ahooks';
import { Spin, Progress } from 'antd';
import styles from './index.module.scss';
import { Button } from '@maxtropy/components';
import { DoubleRightOutlined } from '@ant-design/icons';
import spin_icon from '../../imgs/icon-circle.png';
import budget_icon from '../../imgs/icon-ship.png';
import {
  apiV2AirCompressorOverviewPowerPost,
  V2AirCompressorOverviewPowerPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { OVERVIEW_POLLING_INTERVAL } from '../../utils';

export type SummaryCardData = NonNullable<V2AirCompressorOverviewPowerPostResponse['list']>[number];

const summaryCardOrder = ['淼船线', '东船线', '洲船线'];

const getDisplayCards = (cards: SummaryCardData[]) => {
  const usedCardSet = new Set<SummaryCardData>();

  return summaryCardOrder.map(keyword => {
    const matchedCard = cards.find(card => !usedCardSet.has(card) && card?.circuitName?.includes(keyword));
    const fallbackCard = cards.find(card => !usedCardSet.has(card));
    const displayCard = matchedCard ?? fallbackCard ?? ({} as SummaryCardData);

    usedCardSet.add(displayCard);
    return displayCard;
  });
};

const formatSummaryValue = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '--';
  }
  return value.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
};

const getLoadRatePercent = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return undefined;
  }
  return value > 1 ? value : value * 100;
};

const formatLoadRate = (value?: number) => {
  const percent = getLoadRatePercent(value);
  if (typeof percent !== 'number') {
    return '--';
  }
  return `${percent.toFixed(2)}%`;
};

const SummaryCardItem: React.FC<{ card?: SummaryCardData }> = ({ card }) => {
  const title = card?.circuitName || '--';
  const displayTitle = title === '--' ? title : title.slice(-3);
  const power = formatSummaryValue(card?.power);
  const threshold = formatSummaryValue(card?.threshold);
  const loadRatePercent = getLoadRatePercent(card?.loadRate);
  const loadRateText = formatLoadRate(card?.loadRate);
  const loadRateProgress = Math.max(0, Math.min(loadRatePercent ?? 0, 100));
  const circuitId = card?.circuitId;

  const handleDetailClick = () => {
    if (!circuitId) {
      return;
    }
    const query = new URLSearchParams({
      id: String(circuitId),
      dataType: '5',
      timeResolution: '1M',
    });
    window.open(`${window.DMEPLATFORMSORIGIN}/dmes/statistics/circuit?${query.toString()}`, '_blank');
  };

  return (
    <div className={styles.summaryCard}>
      <div className={styles.summaryCardHeader}>
        <div className={styles.summaryTitleWrap}>
          <div className={styles.icon}>
            <img src={spin_icon} className={styles.icon_spin} alt="" />
            <img src={budget_icon} className={styles.icon_icon} alt="" />
          </div>
          <div className={styles.summaryTitle}>{displayTitle}</div>
        </div>
        <Button type="link" style={{ padding: 0 }} onClick={handleDetailClick} disabled={!circuitId}>
          <div className={styles.linkInner}>
            <span>详情</span>
            <DoubleRightOutlined />
          </div>
        </Button>
      </div>
      <div className={styles.summaryBody}>
        <div className={styles.summaryMetrics}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryRowLabel}>实时功率(kW)</span>
            <span className={styles.hourlyValue}>{power}</span>
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryRow}>
            <span className={styles.summaryRowLabel}>需量阈值(kW)</span>
            <span className={styles.dailyValue}>{threshold}</span>
          </div>
        </div>
        <div className={styles.loadRatePanel}>
          <span className={styles.loadRateLabel}>负载率</span>
          <div className={styles.loadRateBar}>
            <span style={{ width: `${loadRateProgress}%`, opacity: typeof loadRatePercent === 'number' ? 1 : 0 }} />
          </div>
          <Progress
            type="circle"
            percent={loadRateProgress}
            size={42}
            strokeWidth={12}
            strokeColor="#8ceb55"
            trailColor="rgba(74, 144, 226, 0.22)"
            showInfo={false}
            className={styles.loadRateCircle}
          />
          <span className={styles.loadRateValue}>{loadRateText}</span>
        </div>
      </div>
    </div>
  );
};

const SummaryCard: React.FC = () => {
  const { data, loading } = useRequest(() => apiV2AirCompressorOverviewPowerPost({}), {
    pollingInterval: OVERVIEW_POLLING_INTERVAL,
  });
  const cards = data?.list ?? [];
  const displayCards = getDisplayCards(cards);

  return (
    <Spin spinning={loading && !data} wrapperClassName={styles.summaryLoading}>
      <div className={styles.summaryGrid}>
        {displayCards.map((card, index) => (
          <SummaryCardItem key={`${card?.circuitName || 'summary'}-${index}`} card={card} />
        ))}
      </div>
    </Spin>
  );
};

export default SummaryCard;
